import type { TrainingBlock } from '@cadencia/contracts';
import { useLiveQuery } from 'dexie-react-hooks';
import { getSyncState } from './db.js';
import { getActiveBlock } from './repositories/trainingRepo.js';

/**
 * ¿No hay bloque activo, o es que todavía no lo hemos mirado?
 *
 * EL PROBLEMA QUE RESUELVE, y está MEDIDO, no deducido (22/09/2026)
 *
 * Es NEW5-002 otra vez —«no hay fila» y «no la he mirado» son distintos— pero
 * sobre algo que cuesta bastante más que un interruptor: el plan de
 * entrenamiento.
 *
 * `Entrenar` decidía si ofrecer «crear el bloque de readaptación» mirando solo
 * a Dexie. En un dispositivo recién estrenado —un navegador nuevo, una
 * reinstalación— Dexie está vacía mientras la primera bajada viaja, así que la
 * pantalla ofrecía crear un bloque a alguien que YA TENÍA uno activo en el
 * servidor. Y ofrecerlo no es inocuo: el botón crea el bloque, se lo pide a la
 * IA —que se paga— y materializa una semana entera de sesiones.
 *
 * Pasó de verdad, en la primera sesión web de este proyecto: cuatro bloques de
 * readaptación creados en tres minutos sobre una cuenta que ya tenía el suyo,
 * con cinco `training_blocks` activos a la vez. El bloque viejo nunca se cerró
 * —este camino no cierra nada, es el del PRIMER bloque— así que nadie podía
 * detectarlo por ahí. La primera llamada costó 9,5 céntimos; las otras tres
 * fueron `cache_hit` y salieron gratis por pura suerte: el contexto era
 * idéntico porque el gimnasio tampoco había bajado.
 *
 * LA REGLA, LA MISMA QUE `preferenciaIA.ts`
 *
 * Se sabe que no hay bloque si **ha terminado alguna bajada** y aun así no hay
 * ninguno. Antes de eso, la respuesta honesta es «no lo sé», y la pantalla
 * tiene que decir que está sincronizando en vez de proponer crear nada.
 *
 * Las dos lecturas van en la MISMA consulta, por lo mismo que allí: en dos
 * consultas separadas React puede pintar un fotograma con «ya lo sé» de una y
 * el valor viejo de la otra, que es la misma carrera con menos milisegundos.
 *
 * Esto NO rompe el offline-first del §5: un dispositivo que ya sincronizó
 * alguna vez tiene `lastPullAt` y funciona entero sin red. El único que espera
 * es el que nunca ha hablado con el servidor, y ese no tiene plan que enseñar.
 */
export interface EstadoBloqueActivo {
  /** El bloque activo, o `null` si no hay ninguno. */
  bloque: TrainingBlock | null;
  /** `false` mientras «no hay bloque» pueda significar «no lo he mirado». */
  conocido: boolean;
}

export async function leerBloqueActivo(userId: string | null): Promise<EstadoBloqueActivo> {
  if (userId === null) return { bloque: null, conocido: false };

  const bloque = (await getActiveBlock(userId)) ?? null;
  if (bloque !== null) return { bloque, conocido: true };

  const estado = await getSyncState();
  return { bloque: null, conocido: (estado?.lastPullAt ?? null) !== null };
}

/**
 * Lo mismo, en vivo. `undefined` mientras la consulta no ha vuelto: quien lo
 * use tiene que distinguir eso de un resultado, igual que en `preferenciaIA`.
 */
export function useBloqueActivo(userId: string | null): EstadoBloqueActivo | undefined {
  return useLiveQuery(async () => leerBloqueActivo(userId), [userId]);
}
